import { View } from 'react-native'
import React, { useEffect, useRef, useMemo, useState, memo } from 'react'
import { StyleSheet, useUnistyles, UnistylesRuntime } from 'react-native-unistyles'
import { supabase } from '@/data/client'
import CutCard, { CORNER_CLIP } from '@/components/General/CutCard'

function Chart({ref, chartName, parameters = {}, height = 500, autoHeight = false, onClick, onViewData}) {
    const iframeRef1 = useRef(null);
    const [contentHeight, setContentHeight] = useState(0);
    const [reloadKey, setReloadKey] = useState(0);

    const { theme: {scheme} } = useUnistyles()
    const isPortrait = UnistylesRuntime.isPortrait

    const parametersString = useMemo(() =>
        Object.entries(parameters).map(([key, value]) => `${key}=${value}`).join('&'),
        [parameters]
    );

    const iframeSrc = useMemo(() =>
        `${process.env.EXPO_PUBLIC_CHARTS_BASE_URL}/charts/${chartName}?theme=${scheme}&${parametersString}&_r=${reloadKey}`,
        [chartName, scheme, parametersString, reloadKey]
    );

    useEffect(() => {
        if(ref){
            ref.current = iframeRef1;
            // Cross-origin frames block contentWindow.location.reload() —
            // bump the src key instead so the iframe reloads naturally and
            // re-auths via the load listener below.
            ref.current.reload = () => setReloadKey(k => k + 1);
        }

        const ref1 = iframeRef1.current;
        const chartsOrigin = new URL(process.env.EXPO_PUBLIC_CHARTS_BASE_URL).origin;

        const sendToken = async (requestId) => {
            if (!ref1 || !ref1.contentWindow) return;
            const { data, error } = await supabase.auth.getSession();
            if (error || !data.session) {
                console.error('Chart: failed to get session', error);
                return;
            }
            const payload = {
                type: 'CHART_AUTH_TOKEN',
                requestId,
                access_token: data.session.access_token,
                expires_in: data.session.expires_in,
                expires_at: data.session.expires_at,
                token_type: data.session.token_type,
                user: data.session.user,
            };
            const postToken = () => ref1.contentWindow?.postMessage(payload, chartsOrigin);
            if (requestId) {
                postToken();
            } else {
                // Small delay so the iframe's React app has time to mount
                // and attach its message listener before we post the token.
                setTimeout(postToken, 500);
            }
        };

        const receiveMessageFromIframe = (event) => {
            if (event.origin !== chartsOrigin) return;
            if (event.source !== ref1?.contentWindow) return;

            if (event.data?.type === 'CHART_AUTH_TOKEN_REQUEST') {
                sendToken(event.data.requestId);
            }

            if (event.data?.type === 'CHART_CONTENT_HEIGHT') {
                setContentHeight(event.data.height);
            }

            if (event.data?.type === 'CHART_SHOW_DATA') {
                onViewData?.(event.data.payload);
            }

            if (event.data?.type === 'CHART_BAR_CLICK') {
                const { label, value } = event.data.payload;
                onClick?.(label, value);
            }

            if (event.data?.type === 'CHART_REQUEST_FULLSCREEN') {
                const el = iframeRef1.current;
                if (el && el.requestFullscreen) {
                    el.requestFullscreen().then(() => {
                        iframeRef1.current?.contentWindow?.postMessage(
                            { type: 'CHART_FULLSCREEN_ENTER' },
                            '*'
                        );
                    }).catch(() => {});
                }
            }

            if (event.data?.type === 'CHART_FULLSCREEN_EXIT') {
                if (document.fullscreenElement && document.exitFullscreen) {
                    document.exitFullscreen().catch(() => {});
                }
            }
        }

        const handleFullscreenChange = () => {
            if (!document.fullscreenElement) {
                iframeRef1.current?.contentWindow?.postMessage(
                    { type: 'CHART_FULLSCREEN_EXIT' },
                    '*'
                );
            }
        };

        window.addEventListener('message', receiveMessageFromIframe);
        document.addEventListener('fullscreenchange', handleFullscreenChange);

        const handleLoad = () => sendToken();

        if (ref1) {
            ref1.addEventListener('load', handleLoad);
        }

        return () => {
            if (ref1) ref1.removeEventListener('load', handleLoad);
            window.removeEventListener('message', receiveMessageFromIframe);
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, [iframeSrc, chartName, onClick, onViewData]);

    return (
        <CutCard accentColor="primary" style={styles.container(isPortrait)} contentStyle={styles.cardContent}>
            <View style={styles.iframeContainer}>
                <iframe ref={iframeRef1} src={iframeSrc} style={{ width: '100%', height: autoHeight && contentHeight ? contentHeight : height, border: 'none', display: 'block', backgroundColor: 'transparent' }} />
            </View>
        </CutCard>
    )
}

const styles = StyleSheet.create(({shadows}) => ({
    container: (isPortrait) => ({
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: isPortrait ? '100%' : 'calc(50% - 6px)',
        // CutCard owns surface/radius; the shadow stays here so it converts
        // to a clip-path drop-shadow on web that hugs the cut corners.
        boxShadow: shadows.card,
        _web: {
            willChange: 'transform',
        },
    }),
    cardContent: {
        flexGrow: 1,
        alignSelf: 'stretch',
    },
    iframeContainer: {
        flexGrow: 1,
        width: '100%',
        overflow: 'hidden',
        // Transparent wrapper — no surface/shadow/radius, so nothing square
        // sticks out past the CutCard's cut corners. The iframe document
        // paints its own opaque theme bg, so it gets clipped to the same
        // corner silhouette as the card fill.
        backgroundColor: 'transparent',
        _web: {
            clipPath: CORNER_CLIP,
        },
    }
}))

export default memo(Chart)
